import type { IconCustomName } from '@mdvip/shared/components/blocks/Icon';
import type enShared from '@mdvip/shared/locales/en/shared.json';

import type { ThemeCode } from './types';

/** A tile label's key in the `shared` namespace, typed against the locale file so a missing label won't compile. */
export type ThemeLabelKey = `awpDashboard.themes.${keyof (typeof enShared)['awpDashboard']['themes']}`;

/** The design's health-theme marks: custom SVGs in the shared Icon, not Font Awesome glyphs. */
export type ThemeIconName = Extract<IconCustomName, `theme${string}`>;

/** A theme's name in the dashboard URL (`?theme=heart`). Ours, so the URL doesn't change if labs-api renames a code. */
export type ThemeSlug = 'heart' | 'metabolic' | 'brain' | 'longevity';

/** One of the five data-viz tokens a theme takes its accent from. */
export type ChartToken = `--chart-${1 | 2 | 3 | 4 | 5}`;

/** How a dashboard tile looks: its label, glyph and accent. The Health Summary has one too, though it isn't a theme. */
export interface TileConfig {
  /** Key in the `shared` namespace. The display name is ours to own; labs-api sends only the code. */
  i18nKey: ThemeLabelKey;
  icon: ThemeIconName;
  /** The data-viz token carrying the tile's accent. */
  chartToken: ChartToken;
}

export interface ThemeConfig extends TileConfig {
  slug: ThemeSlug;
}

/** The Health Summary tile, which leads the strip. Not a `ThemeCode`, so it has no slug: it's the URL's default. */
export const SUMMARY_CONFIG: TileConfig = {
  i18nKey: 'awpDashboard.themes.summary',
  icon: 'themeSummary',
  chartToken: '--chart-2',
};

// Typed against the generated enum, so a theme labs-api adds won't compile until it's configured here.
export const THEME_CONFIG: Record<ThemeCode, ThemeConfig> = {
  HEART_HEALTH: { slug: 'heart', i18nKey: 'awpDashboard.themes.heartHealth', icon: 'themeHeart', chartToken: '--chart-4' },
  METABOLIC_HEALTH: {
    slug: 'metabolic',
    i18nKey: 'awpDashboard.themes.metabolicHealth',
    icon: 'themeMetabolic',
    chartToken: '--chart-1',
  },
  BRAIN_COGNITIVE: { slug: 'brain', i18nKey: 'awpDashboard.themes.brainCognitive', icon: 'themeBrain', chartToken: '--chart-3' },
  LONGEVITY_WELLNESS: {
    slug: 'longevity',
    i18nKey: 'awpDashboard.themes.longevityWellness',
    icon: 'themeLongevity',
    chartToken: '--chart-5',
  },
};

/** Display order of the theme cards. The enum carries no order; this is the design's. */
export const THEME_ORDER: readonly ThemeCode[] = ['HEART_HEALTH', 'METABOLIC_HEALTH', 'BRAIN_COGNITIVE', 'LONGEVITY_WELLNESS'];

const THEME_CODE_BY_SLUG: ReadonlyMap<string, ThemeCode> = new Map(
  THEME_ORDER.map((code) => [THEME_CONFIG[code].slug, code]),
);

/** The theme a URL slug names, or `undefined` for anything else. A `Map`, so inherited names like `constructor` don't match. */
export const themeCodeOfSlug = (slug: string): ThemeCode | undefined => THEME_CODE_BY_SLUG.get(slug);
